import { AutoSaveIndicator, Button, Css, useModal } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { PageHeader } from "src/routes/layout/PageHeader";
import { PlanPackageTakeoffPage_TakeoffDetailFragment } from "src/routes/libraries/plan-package/takeoffs/PlanPackageTakeoffPage.generated";
import { createPlanPackageIncomingSlotReviewUrl } from "src/RouteUrls";
import { fail, pluralize } from "src/utils";
import { PublishAggregateVersionModal } from "../../details/components/PublishAggregateVersionModal";

export type TakeoffHeaderProps = {
  planPackage: PlanPackageTakeoffPage_TakeoffDetailFragment;
  pendingSlotCount: number;
};

export function TakeoffsHeader(props: TakeoffHeaderProps) {
  const { planPackage, pendingSlotCount } = props;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const designerModifiedSlotsEnabled = useFeatureFlag(FeatureFlagType.DesignerModifiedSlots);

  const { aggregateDraft, name } = planPackage;

  return (
    <PageHeader
      xss={Css.bgTransparent.bn.mb0.$}
      titleSize="xl2"
      title="Takeoff Manager"
      left={<AutoSaveIndicator />}
      right={
        <>
          {designerModifiedSlotsEnabled && pendingSlotCount > 0 && (
            <Button
              label={`${pendingSlotCount} ${pluralize(pendingSlotCount, "slot")} to review`}
              onClick={() =>
                navigate(
                  createPlanPackageIncomingSlotReviewUrl(
                    planPackage.id,
                    planPackage.version?.id ?? fail("Plan package version is required"),
                  ),
                )
              }
              variant="caution"
              icon="todo"
            />
          )}
          <ReadyPlanVersionHeader readyPlan={planPackage} versionId={planPackage.version?.id} />
          {/* TODO: Work with Product & Design to determine when/how this can be Published */}
          <Button
            size="md"
            disabled={!aggregateDraft?.canPublish.allowed}
            tooltip={aggregateDraft?.canPublish.disabledReasons.map((reason) => reason.message).join(", ")}
            label="Publish"
            onClick={() =>
              openModal({
                content: (
                  <PublishAggregateVersionModal
                    draftAggregateVersion={aggregateDraft ?? fail("No draft aggregate version found")}
                    copyText="This update will be made available to any Design Package and Product Offering that reference it."
                    readyPlanName={name}
                  />
                ),
              })
            }
          />
        </>
      }
    />
  );
}
